import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconAdd from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton, TextField } from '@mui/material';
import { DataProviderInstance } from '../../api/DataProvider';
import { MAX_PRODUCTS, nextProductColor } from '../../pages/appointment/components/applicationMap/applicationProducts';
import { formatQuantity, toQuantity } from '../../pages/appointment/components/applicationMap/applicationMarks';
import styles from './ApplicationProductsPopup.module.css';

const MAX_NAME_LENGTH = 80;
const MAX_UNIT_LENGTH = 12;

const toRow = (product) => ({
  id: product.id == null ? null : Number(product.id),
  name: product.name || '',
  unit: product.unit || '',
  color: String(product.color || '#5A80FB').toUpperCase(),
  defaultQuantity: formatQuantity(product.defaultQuantity),
});

/**
 * Lista de produtos do mapa de aplicação da clínica: nome, unidade, cor e
 * quantidade padrão de cada um. "Salvar" grava a lista inteira
 * (`onSaved(lista gravada)`); as marcações já feitas guardam os próprios
 * dados e não mudam. Fechar descarta.
 */
function ApplicationProductsPopup({ products, onSaved, OnHide }) {
  const { SetApplicationProducts } = DataProviderInstance();
  const [rows, setRows] = useState(() => products.map(toRow));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const update = (index, patch) => setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  function addRow() {
    if (rows.length >= MAX_PRODUCTS) return;
    setRows((current) => [...current, { id: null, name: '', unit: 'ml', color: nextProductColor(current), defaultQuantity: '1' }]);
  }

  function removeRow(index) {
    setRows((current) => current.filter((_, i) => i !== index));
  }

  function saveHandler() {
    const cleaned = rows
      .map((row) => ({ ...row, name: row.name.trim(), unit: row.unit.trim(), defaultQuantity: toQuantity(row.defaultQuantity, 1) }))
      .filter((row) => row.name !== '');
    if (cleaned.length === 0) {
      setError('Mantenha ao menos um produto com nome.');
      return;
    }
    setError('');
    setSaving(true);
    SetApplicationProducts(cleaned, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        onSaved(response.data || []);
        OnHide();
      } else {
        setError((response && response.error) || 'Não foi possível salvar os produtos.');
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <span className={styles.help}>Cada produto tem a sua cor na figura e uma quantidade padrão por marcação.</span>

      <div className={styles.rows}>
        {rows.map((row, index) => (
          <div key={index} className={styles.row}>
            <label className={styles.swatch} style={{ background: row.color }} title="Cor na figura">
              <input
                type="color"
                value={row.color}
                onChange={(event) => update(index, { color: event.target.value.toUpperCase() })}
                aria-label="Cor do produto"
              />
            </label>
            <TextField
              size="small"
              label="Produto"
              value={row.name}
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
              onChange={(event) => update(index, { name: event.target.value })}
              className={styles.name}
            />
            <TextField
              size="small"
              label="Unidade"
              value={row.unit}
              inputProps={{ maxLength: MAX_UNIT_LENGTH }}
              onChange={(event) => update(index, { unit: event.target.value })}
              className={styles.unit}
            />
            <TextField
              size="small"
              label="Padrão"
              value={row.defaultQuantity}
              inputProps={{ inputMode: 'decimal', maxLength: 8 }}
              onChange={(event) => update(index, { defaultQuantity: event.target.value })}
              className={styles.quantity}
            />
            <IconButton size="small" onClick={() => removeRow(index)} aria-label="Remover produto" disabled={rows.length <= 1}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <CustomButton
          variant="outlined"
          style="secondary"
          label="Adicionar produto"
          icon={<IconAdd />}
          onClick={addRow}
          enabled={rows.length < MAX_PRODUCTS}
        />
        {error && <span className={styles.error}>{error}</span>}
      </div>
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={() => OnHide()} />
      <CustomButton variant="contained" style="primary" label="Salvar" icon={<IconSave />} onClick={saveHandler} enabled={!saving} />
    </div>
  );

  return (
    <PopupBase
      Title="Produtos do mapa de aplicação"
      Content={content}
      Footer={footer}
      OnClose={() => OnHide()}
      maxWidth="sm"
    />
  );
}

export default ApplicationProductsPopup;
